import React, { useState, useCallback, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { qaApi } from "../../../services/api/qaApi";
import { useOptimisticUpdate } from "../../../hooks/useOptimisticUpdate";
import {
  FiChevronUp,
  FiChevronDown,
  FiAward,
  FiCheckCircle,
  FiMessageSquare,
  FiEdit3,
} from "react-icons/fi";
import AnswersList from "./AnswersList";
import AnswerForm from "./AnswerForm";
import BountyModal from "../BountyModal";
import { useUserContext } from "../../../context/UserContext";
import "./QuestionPost.css";

const QuestionPost = ({ post, onUpdate }) => {
  const { user: currentAuthUser } = useUserContext();
  const [showAnswers, setShowAnswers] = useState(false); // Don't show answers by default - lazy load
  const [showAnswerForm, setShowAnswerForm] = useState(false); // Hide form by default
  const [answers, setAnswers] = useState(post.question?.answers || []); // Local answers state

  const [loadingAnswers, setLoadingAnswers] = useState(false); // Loading state for answers
  const [hasMoreAnswers, setHasMoreAnswers] = useState(false); // Pagination state
  const [answersOffset, setAnswersOffset] = useState(0); // Pagination offset
  const [sortBy, setSortBy] = useState("accepted"); // Sort option
  const [showBountyModal, setShowBountyModal] = useState(false);
  const [addingBounty, setAddingBounty] = useState(false);
  // Use optimized counts from backend instead of array lengths
  const [score, setScore] = useState(
    (post.question?.upvoteCount || 0) - (post.question?.downvoteCount || 0)
  );
  // Use userVote from backend (no need to check arrays anymore!)
  const [userVote, setUserVote] = useState(post.question?.userVote || null);
  const { optimisticUpdate } = useOptimisticUpdate();

  const currentUserId = useMemo(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.id || user?._id || user?.userId;
  }, []);

  // Sync userVote from backend feed response
  React.useEffect(() => {
    // Backend ALREADY includes userVote in feed response!
    if (post.question?.userVote !== undefined) {
      setUserVote(post.question.userVote);
    }
  }, [post.question?.userVote]);

  // Sync score from backend when post updates
  React.useEffect(() => {
    const newScore =
      (post.question?.upvoteCount || 0) - (post.question?.downvoteCount || 0);
    setScore(newScore);
  }, [post.question?.upvoteCount, post.question?.downvoteCount]);

  // Use answerCount from backend or fallback to local answers length
  const answerCount = post.question?.answerCount ?? answers.length;

  // Lazy load answers when user clicks "Show Answers"
  const handleToggleAnswers = useCallback(async () => {
    if (!showAnswers && answers.length === 0 && answerCount > 0) {
      // Fetch first batch of answers (10 answers)
      setLoadingAnswers(true);
      try {
        const response = await qaApi.getAnswers(post._id, {
          limit: 10,
          offset: 0,
          sortBy,
        });
        const data = response.data;
        setAnswers(data.answers || []);
        setHasMoreAnswers(data.pagination?.hasMore || false);
        setAnswersOffset(data.pagination?.nextOffset || 0);
      } catch (error) {
        alert("Failed to load answers. Please try again.");
      } finally {
        setLoadingAnswers(false);
      }
    }
    setShowAnswers(!showAnswers);
  }, [showAnswers, answers.length, answerCount, post._id, sortBy]);

  // Load more answers (pagination)
  const handleLoadMoreAnswers = useCallback(async () => {
    if (!hasMoreAnswers || loadingAnswers) return;

    setLoadingAnswers(true);
    try {
      const response = await qaApi.getAnswers(post._id, {
        limit: 10,
        offset: answersOffset,
        sortBy,
      });
      const data = response.data;
      setAnswers((prev) => [...prev, ...(data.answers || [])]);
      setHasMoreAnswers(data.pagination?.hasMore || false);
      setAnswersOffset(data.pagination?.nextOffset || 0);
    } catch (error) {
      alert("Failed to load more answers. Please try again.");
    } finally {
      setLoadingAnswers(false);
    }
  }, [hasMoreAnswers, loadingAnswers, answersOffset, post._id, sortBy]);

  // Sync answers from props if updated externally (e.g., new answer added)
  React.useEffect(() => {
    if (post.question?.answers && post.question.answers.length > 0) {
      setAnswers(post.question.answers);
    }
  }, [post.question?.answers]);

  // Reload answers when sort order changes
  React.useEffect(() => {
    if (showAnswers && answers.length > 0) {
      // Reload first batch with new sort
      const reloadAnswers = async () => {
        setLoadingAnswers(true);
        try {
          const response = await qaApi.getAnswers(post._id, {
            limit: 10,
            offset: 0,
            sortBy,
          });
          const data = response.data;
          setAnswers(data.answers || []);
          setHasMoreAnswers(data.pagination?.hasMore || false);
          setAnswersOffset(data.pagination?.nextOffset || 0);
        } catch (error) {
          // Silent fail for sort change
        } finally {
          setLoadingAnswers(false);
        }
      };
      reloadAnswers();
    }
  }, [sortBy]); // Only trigger when sortBy changes

  // Handle vote with optimistic update
  const handleVote = useCallback(
    async (voteType) => {
      const previousScore = score;
      const previousVote = userVote;

      let newScore = score;
      let newVote = voteType;

      if (userVote === voteType) {
        newScore = voteType === "upvote" ? score - 1 : score + 1;
        newVote = null;
      } else if (userVote) {
        newScore = voteType === "upvote" ? score + 2 : score - 2;
      } else {
        newScore = voteType === "upvote" ? score + 1 : score - 1;
      }

      await optimisticUpdate({
        updateFn: () => {
          setScore(newScore);
          setUserVote(newVote);
        },
        apiFn: () => qaApi.voteQuestion(post._id, voteType),
        rollbackFn: () => {
          setScore(previousScore);
          setUserVote(previousVote);
        },
        onSuccess: (response) => {
          // VOTE endpoint returns: { success: true, data: { action, score, upvotes, downvotes } }
          const voteData = response.data.data || response.data;

          // Use backend's calculated score (source of truth)
          const backendScore =
            voteData.score ||
            (voteData.upvotes || 0) - (voteData.downvotes || 0);
          setScore(backendScore);

          // Derive userVote from action (VOTE endpoint uses "action", FEED uses "userVote")
          let newUserVote = null;
          if (voteData.action === "upvoted") {
            newUserVote = "upvote";
          } else if (voteData.action === "downvoted") {
            newUserVote = "downvote";
          }
          // If action is "removed_upvote" or "removed_downvote", userVote stays null

          setUserVote(newUserVote);

          if (onUpdate) {
            onUpdate(post._id, {
              question: {
                ...post.question,
                upvoteCount: voteData.upvotes,
                downvoteCount: voteData.downvotes,
                userVote: newUserVote,
              },
            });
          }
        },
      });
    },
    [score, userVote, post._id, post.question, onUpdate, optimisticUpdate]
  );

  // Add bounty
  const handleAddBounty = useCallback(
    async (amount) => {
      setAddingBounty(true);
      try {
        const response = await qaApi.addBounty(post._id, amount);
        setShowBountyModal(false);
        if (onUpdate) {
          onUpdate(post._id, {
            question: {
              ...post.question,
              bounty: response.data.data.bounty,
            },
          });
        }
      } catch (error) {
        alert(error.response?.data?.message || "Failed to add bounty");
      } finally {
        setAddingBounty(false);
      }
    },
    [post._id, post.question, onUpdate]
  );

  // Fetch user reputation from the API
  const [userReputation, setUserReputation] = useState(currentAuthUser?.reputation || 0);
  const [reputationLoaded, setReputationLoaded] = useState(false);

  // Fetch reputation when bounty modal is opened
  useEffect(() => {
    if (showBountyModal && !reputationLoaded) {
      const fetchReputation = async () => {
        try {
          const token = JSON.parse(localStorage.getItem("user"))?.token;
          const userId = currentUserId;
          
          const response = await fetch(`http://localhost:8000/api/users/${userId}/reputation`, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          const data = await response.json();
          
          if (data.success && data.data?.reputation !== undefined) {
            setUserReputation(data.data.reputation);
          }
          setReputationLoaded(true);
        } catch (error) {
          // Fallback to auth user reputation
          setUserReputation(currentAuthUser?.reputation || 0);
        }
      };
      fetchReputation();
    }
  }, [showBountyModal, reputationLoaded, currentAuthUser?.reputation, currentUserId]);

  const hasAcceptedAnswer = post.question?.acceptedAnswer != null;
  const isQuestionAuthor = String(post.author?._id) === String(currentUserId);

  return (
    <div className="cbx-qp">
      {/* Question Header with Vote Section */}
      <div className="cbx-qp__main">
        {/* Vote Section */}
        <div className="cbx-qp__votes">
          <button
            className={`cbx-qp__vote-btn cbx-qp__vote-btn--up ${
              userVote === "upvote" ? "cbx-qp__vote-btn--active" : ""
            }`}
            onClick={() => handleVote("upvote")}
            aria-label="Upvote question"
            title="This question is useful"
          >
            <FiChevronUp />
          </button>
          <span className="cbx-qp__score">{score}</span>
          <button
            className={`cbx-qp__vote-btn cbx-qp__vote-btn--down ${
              userVote === "downvote" ? "cbx-qp__vote-btn--active" : ""
            }`}
            onClick={() => handleVote("downvote")}
            aria-label="Downvote question"
            title="This question is not useful"
          >
            <FiChevronDown />
          </button>
        </div>

        {/* Question Content */}
        <div className="cbx-qp__content">
          <h2 className="cbx-qp__title">
            {post.question?.title || "Question"}
          </h2>

          {post.content && (
            <div className="cbx-qp__body">{post.content}</div>
          )}

          {/* Tags */}
          {post.question?.tags && post.question.tags.length > 0 && (
            <div className="cbx-qp__tags">
              {post.question.tags.map((tag, index) => (
                <span key={index} className="cbx-qp__tag">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Meta */}
          <div className="cbx-qp__meta">
            <div className="cbx-qp__meta-stats">
              <span className="cbx-qp__meta-item">
                {post.question?.views || 0} views
              </span>
              <span className="cbx-qp__meta-sep">&middot;</span>
              <span className="cbx-qp__meta-item">
                {answerCount} {answerCount === 1 ? "answer" : "answers"}
              </span>
            </div>
            {post.question?.difficulty && (
              <span className={`cbx-qp__difficulty cbx-qp__difficulty--${post.question.difficulty}`}>
                {post.question.difficulty}
              </span>
            )}
          </div>

          {/* Status indicators */}
          <div className="cbx-qp__status">
            {post.question?.bounty > 0 && (
              <div className="cbx-qp__bounty">
                <FiAward className="cbx-qp__bounty-icon" />
                <span className="cbx-qp__bounty-amount">+{post.question.bounty}</span>
                <span className="cbx-qp__bounty-label">reputation bounty</span>
              </div>
            )}
            {hasAcceptedAnswer && (
              <div className="cbx-qp__solved">
                <FiCheckCircle />
                <span>Accepted Answer</span>
              </div>
            )}
            {isQuestionAuthor && !post.question?.bounty && (
              <button
                onClick={() => setShowBountyModal(true)}
                className="cbx-qp__bounty-btn"
              >
                <FiAward /> Add Bounty
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Answers Section */}
      <div className="cbx-qp__answers-header">
        <h3 className="cbx-qp__answers-count">
          {answerCount} {answerCount === 1 ? "Answer" : "Answers"}
        </h3>
        <div className="cbx-qp__actions">
          {answerCount > 0 && (
            <button
              className="cbx-qp__action-btn cbx-qp__action-btn--secondary"
              onClick={handleToggleAnswers}
              disabled={loadingAnswers}
            >
              <FiMessageSquare />
              {loadingAnswers
                ? "Loading..."
                : showAnswers
                ? "Hide Answers"
                : "Show Answers"}
            </button>
          )}
          {!showAnswerForm && (
            <button
              className="cbx-qp__action-btn cbx-qp__action-btn--primary"
              onClick={() => setShowAnswerForm(true)}
            >
              <FiEdit3 /> Write Answer
            </button>
          )}
        </div>
      </div>

      {/* Answers List */}
      <AnimatePresence>
        {showAnswers && answerCount > 0 && (
          <motion.div
            className="cbx-qp__answers-list"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="cbx-qp__sort-bar">
              <span className="cbx-qp__sort-label">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="cbx-qp__sort-select"
              >
                <option value="accepted">Accepted First</option>
                <option value="votes">Highest Votes</option>
                <option value="recent">Most Recent</option>
              </select>
            </div>

            <AnswersList
              postId={post._id}
              answers={answers}
              acceptedAnswerId={post.question?.acceptedAnswer}
              isQuestionAuthor={isQuestionAuthor}
              bounty={post.question?.bounty || 0}
              onUpdate={onUpdate}
            />

            {hasMoreAnswers && (
              <div className="cbx-qp__load-more">
                <button
                  onClick={handleLoadMoreAnswers}
                  disabled={loadingAnswers}
                  className="cbx-qp__load-more-btn"
                >
                  {loadingAnswers ? "Loading..." : "Load More Answers"}
                </button>
                <span className="cbx-qp__remaining">
                  {answerCount - answers.length} more{" "}
                  {answerCount - answers.length === 1 ? "answer" : "answers"}
                </span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Answer Form */}
      <AnimatePresence>
        {showAnswerForm && (
          <motion.div
            className="cbx-qp__answer-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
          >
            <AnswerForm
              postId={post._id}
              onAnswerAdded={(newAnswer) => {
                const updatedAnswers = [...answers, newAnswer];
                setAnswers(updatedAnswers);

                if (onUpdate) {
                  onUpdate(post._id, {
                    question: {
                      ...post.question,
                      answerCount: (post.question?.answerCount || 0) + 1,
                    },
                  });
                }

                setShowAnswerForm(false);
                setShowAnswers(true);
              }}
              onCancel={() => setShowAnswerForm(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bounty Modal */}
      <BountyModal
        isOpen={showBountyModal}
        onClose={() => setShowBountyModal(false)}
        onConfirm={handleAddBounty}
        userReputation={userReputation}
        loading={addingBounty}
      />
    </div>
  );
};

export default React.memo(QuestionPost);
